/**
 * AI Settings Utils
 * 
 * Model selection for Gemini-powered pipeline steps:
 *   - Spec Extract Model (Step 3.5)
 *   - Classification Model (Mfr/Vendor AI)
 *   - Extraction Engine (Step 6) — Flan-T5 or Gemini+Memory
 *   - Gemini Extraction Model — which Gemini model for Step 6
 */

// Every AI step offers the same models, in the same order (cheapest first).
const AVAILABLE_MODELS = [
    { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite', provider: 'Google', desc: 'Lowest cost' },
    { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', provider: 'OpenAI', desc: 'Low cost — uses the OpenAI API key' },
    { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', provider: 'Google', desc: 'Higher accuracy — about 6× the cost' },
];

// Step 6 can also use the local custom-trained model
const LOCAL_FLAN_T5_MODEL = { id: 'flan-t5', label: 'Flan-T5 Large (custom trained)', provider: 'Local', desc: 'Needs the local inference server; falls back to Gemini' };

const GEMINI_EXTRACTION_MODELS = [
    { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite', tier: 'fast', desc: 'Fast, low cost extraction' },
    { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', tier: 'balanced', desc: 'Higher accuracy' },
];

const EXTRACTION_ENGINES = [
    {
        id: 'flan-t5',
        label: 'Flan-T5 Large (Custom Trained)',
        description: 'Local fine-tuned model. Requires inference server running.',
        icon: '🤖',
        color: 'orange',
    },
    {
        id: 'gemini-fewshot',
        label: 'Gemini + Dynamic Few-Shot Memory',
        description: 'Gemini API with examples from approved training data.',
        icon: '✨',
        color: 'cyan',
    },
    {
        id: 'openai-luna',
        label: 'GPT-5.6 Luna + Dynamic Few-Shot Memory',
        description: 'OpenAI Responses API using the backend OPENAI_API_KEY.',
        icon: '🌙',
        color: 'violet',
    },
];

const DEFAULT_SPEC_EXTRACT_MODEL = 'gemini-3.1-flash-lite';
const DEFAULT_CLASSIFICATION_MODEL = 'gemini-3.5-flash';
const DEFAULT_EXTRACTION_ENGINE = 'gemini-fewshot';
const DEFAULT_GEMINI_EXTRACTION_MODEL = 'gemini-3.1-flash-lite';
const DEFAULT_CONDITION_GENERATION_MODEL = 'gemini-3.1-flash-lite';

const DEFAULT_AGENT_SETTINGS = Object.freeze({
    enabled: false,
    triggerOnNoResults: true,
    plannerModel: DEFAULT_CLASSIFICATION_MODEL,
    maxSearchRounds: 2,
    maxQueriesPerRound: 4,
    resultsPerQuery: 5,
    maxCandidateUrls: 15,
    timeoutSeconds: 90,
});

const RETIRED_MODEL_REPLACEMENTS = Object.freeze({
    'gemini-1.5-flash': DEFAULT_GEMINI_EXTRACTION_MODEL,
    'gemini-2.0-flash-lite': DEFAULT_GEMINI_EXTRACTION_MODEL,
    'gemini-2.5-flash-lite': DEFAULT_GEMINI_EXTRACTION_MODEL,
    'gemini-3.1-flash-lite-preview': DEFAULT_GEMINI_EXTRACTION_MODEL,
    'gemini-2.0-flash': DEFAULT_CLASSIFICATION_MODEL,
    'gemini-2.5-flash': DEFAULT_CLASSIFICATION_MODEL,
    'gemini-2.5-pro': DEFAULT_CLASSIFICATION_MODEL,
});

export function normalizeGeminiModel(model, fallback = DEFAULT_GEMINI_EXTRACTION_MODEL) {
    const selected = String(model || fallback).trim();
    return RETIRED_MODEL_REPLACEMENTS[selected] || selected;
}

/**
 * Check if settings are configured
 */
export function hasAISettings() {
    return localStorage.getItem('aiSettings') !== null;
}


/**
 * Get the spec extract model setting from localStorage.
 */
export function getSpecExtractModel() {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            return normalizeGeminiModel(parsed.specExtractModel, DEFAULT_SPEC_EXTRACT_MODEL);
        }
    } catch (e) {
        console.error('Failed to load spec extract model:', e);
    }
    return DEFAULT_SPEC_EXTRACT_MODEL;
}

/**
 * Get the classification model setting from localStorage.
 */
export function getClassificationModel() {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            return normalizeGeminiModel(parsed.classificationModel, DEFAULT_CLASSIFICATION_MODEL);
        }
    } catch (e) {
        console.error('Failed to load classification model:', e);
    }
    return DEFAULT_CLASSIFICATION_MODEL;
}

/**
 * Get the extraction engine setting from localStorage.
 * Returns 'flan-t5' or 'gemini-fewshot'.
 */
export function getExtractionEngine() {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            return parsed.extractionEngine || DEFAULT_EXTRACTION_ENGINE;
        }
    } catch (e) {
        console.error('Failed to load extraction engine:', e);
    }
    return DEFAULT_EXTRACTION_ENGINE;
}

/**
 * Get the Gemini model to use for Step 6 extraction.
 * Only relevant when extraction engine is 'gemini-fewshot'.
 */
export function getGeminiExtractionModel() {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            return normalizeGeminiModel(parsed.geminiExtractionModel, DEFAULT_GEMINI_EXTRACTION_MODEL);
        }
    } catch (e) {
        console.error('Failed to load gemini extraction model:', e);
    }
    return DEFAULT_GEMINI_EXTRACTION_MODEL;
}

export function getConditionGenerationModel() {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            return normalizeGeminiModel(parsed.conditionGenerationModel, DEFAULT_CONDITION_GENERATION_MODEL);
        }
    } catch (e) {
        console.error('Failed to load condition generation model:', e);
    }
    return DEFAULT_CONDITION_GENERATION_MODEL;
}

export function getAgentSettings() {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                ...DEFAULT_AGENT_SETTINGS,
                ...(parsed.agent || {}),
                plannerModel: normalizeGeminiModel(parsed.agent?.plannerModel, DEFAULT_AGENT_SETTINGS.plannerModel),
            };
        }
    } catch (e) {
        console.error('Failed to load Agentic AI settings:', e);
    }
    return { ...DEFAULT_AGENT_SETTINGS };
}

// ══════════════════════════════════════════════════════════════════════
//  PIPELINE STEP TOGGLES
// ══════════════════════════════════════════════════════════════════════


const PIPELINE_STEPS = [
    { id: 'step0',   label: 'DB Check',           step: '0',   icon: '🗃️', desc: 'Check Master DB for existing data',        defaultEnabled: true },
    { id: 'step1',   label: 'Supplier Search',     step: '1',   icon: '🔍', desc: 'Serper search using Supplier + PN (+ Description when ON above)', defaultEnabled: true },
    { id: 'step1_5', label: 'DB MFR Priority',     step: '1.5', icon: '🏭', desc: 'Check DB for manufacturer data',           defaultEnabled: true },
    { id: 'step2',   label: 'AI Link Ranking',     step: '2',   icon: '🤖', desc: 'AI picks Manufacturer & Vendor links with confidence (OFF → top search result)', defaultEnabled: true },
    { id: 'step2_5', label: 'MFR Validation',      step: '2.5', icon: '✅', desc: 'Validate manufacturer routing',            defaultEnabled: true },
    { id: 'step3',   label: 'Scraping',            step: '3',   icon: '🕸️', desc: 'Firecrawl scrape target URLs',             defaultEnabled: true },
    { id: 'step3_5', label: 'Content Deep Clean',  step: '3.5', icon: '🧹', desc: 'Code-based cleanup of scraped content (junk, duplicates, commercial text)', defaultEnabled: true },
    { id: 'step4',   label: 'Spec Extraction',     step: '4',   icon: '📋', desc: 'AI extract specs from scraped content',    defaultEnabled: true },
    { id: 'step4_5', label: 'Noun/Modifier Clean', step: '4.5', icon: '🏷️', desc: 'Clean N/M from extracted content',         defaultEnabled: true },
    { id: 'step6',   label: 'AI Extraction',       step: '6',   icon: '⚡', desc: 'Final AI attribute extraction',           defaultEnabled: true },
    { id: 'step6_5', label: 'Custom Code: PN & N/M Cleanup', step: '6.5', icon: '🧼', desc: 'Remove extracted values that exactly match the Part Number or Noun/Modifier', defaultEnabled: true },
];

/**
 * Default pipeline step enabled states (all ON)
 */
const DEFAULT_PIPELINE_STEPS = Object.fromEntries(
    PIPELINE_STEPS.map(s => [s.id, s.defaultEnabled])
);

/**
 * Get whether a specific pipeline step is enabled.
 * @param {string} stepId - e.g. 'step0', 'step1', 'step2_5'
 * @returns {boolean}
 */
export function getPipelineStepEnabled(stepId) {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.pipelineSteps && typeof parsed.pipelineSteps[stepId] === 'boolean') {
                return parsed.pipelineSteps[stepId];
            }
        }
    } catch (e) {
        console.error('Failed to load pipeline step setting:', e);
    }
    // Default: check PIPELINE_STEPS config
    const step = PIPELINE_STEPS.find(s => s.id === stepId);
    return step ? step.defaultEnabled : true;
}

/**
 * Description search toggle (Pipeline settings).
 * ON (default): Serper gets the part number(s) AND the product description.
 * OFF: Serper gets the part number(s) only; the rest of the pipeline is unchanged.
 * @returns {boolean}
 */
export const DEFAULT_SEARCH_WITH_DESCRIPTION = true;

export function getSearchWithDescription() {
    try {
        const parsed = JSON.parse(localStorage.getItem('aiSettings') || 'null');
        if (parsed && typeof parsed.searchWithDescription === 'boolean') return parsed.searchWithDescription;
    } catch (e) {
        console.error('Failed to load description search setting:', e);
    }
    return DEFAULT_SEARCH_WITH_DESCRIPTION;
}

/**
 * Get all pipeline step enabled states as an object.
 * @returns {Object<string, boolean>}
 */
export function getAllPipelineSteps() {
    try {
        const saved = localStorage.getItem('aiSettings');
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.pipelineSteps) {
                return { ...DEFAULT_PIPELINE_STEPS, ...parsed.pipelineSteps };
            }
        }
    } catch (e) {
        console.error('Failed to load pipeline steps:', e);
    }
    return { ...DEFAULT_PIPELINE_STEPS };
}

export {
    AVAILABLE_MODELS,
    LOCAL_FLAN_T5_MODEL,
    GEMINI_EXTRACTION_MODELS,
    EXTRACTION_ENGINES,
    DEFAULT_SPEC_EXTRACT_MODEL,
    DEFAULT_CLASSIFICATION_MODEL,
    DEFAULT_EXTRACTION_ENGINE,
    DEFAULT_GEMINI_EXTRACTION_MODEL,
    DEFAULT_CONDITION_GENERATION_MODEL,
    DEFAULT_AGENT_SETTINGS,
    PIPELINE_STEPS,
    DEFAULT_PIPELINE_STEPS,
};

export default {
    hasAISettings,
    getSpecExtractModel,
    getClassificationModel,
    getExtractionEngine,
    getGeminiExtractionModel,
    getConditionGenerationModel,
    getPipelineStepEnabled,
    getSearchWithDescription,
    getAllPipelineSteps,
    getAgentSettings,
    AVAILABLE_MODELS,
    GEMINI_EXTRACTION_MODELS,
    EXTRACTION_ENGINES,
    DEFAULT_SPEC_EXTRACT_MODEL,
    DEFAULT_CLASSIFICATION_MODEL,
    DEFAULT_EXTRACTION_ENGINE,
    DEFAULT_GEMINI_EXTRACTION_MODEL,
    DEFAULT_CONDITION_GENERATION_MODEL,
    DEFAULT_AGENT_SETTINGS,
    PIPELINE_STEPS,
    DEFAULT_PIPELINE_STEPS,
    normalizeGeminiModel,
};
